import { useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { observer } from "@legendapp/state/react";
import {
  feedCurrentIndex$,
  chapters$,
  feedHasMore$,
  nextChapter,
  previousChapter,
  feedVerticalScrollRequest$,
} from "../state/feedState";
import { isPaused$, lessonDataCache$, maxPositionReachedMs$ } from "../state/videoState";
import { likedChapters$ } from "../state/userState";
import { VideoLayout } from "./VideoLayout";
import { HorizontalLessonCarousel } from "./HorizontalLessonCarousel";
import { EndOfContentScreen } from "./EndOfContentScreen";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

/** Distance from the end (in chapters) at which we prefetch the next page. */
const LOAD_MORE_THRESHOLD = 3;

/**
 * After a touch lifts, wait this long for momentum scrolling to start. If no
 * scroll fired in that window the gesture was a slow drag that left the list
 * mid-panel, so we snap it to the nearest panel ourselves.
 */
const MOMENTUM_SETTLE_DELAY_MS = 64;
/** Don't bother snapping if we're already within this many px of the target. */
const SNAP_TOLERANCE_PX = 1;

/**
 * Fraction of a clip's own duration the user must have reached for the video to
 * count as COMPLETED (vs SKIPPED) when they advance away. Mirrors the same
 * "~watched to the end" bar the 90% activity-regen trigger uses — a video the
 * user reached the back of is a completion; bailing earlier is a skip.
 */
const VIDEO_COMPLETED_FRACTION = 0.9;

interface VerticalVideoCarouselProps {
  onToggleLike: (chapterId: number, currentlyLiked: boolean) => void;
  onChapterViewed: (chapterId: number) => void;
  onLoadMore: () => void;
}

const VerticalVideoCarousel = observer(function VerticalVideoCarousel({
  onToggleLike,
  onChapterViewed,
  onLoadMore,
}: VerticalVideoCarouselProps) {
  const chapters = chapters$.get();
  const currentIndex = feedCurrentIndex$.get();
  const hasMore = feedHasMore$.get();
  const { trackFeedVideoScrolled, trackFeedVideoViewed, trackFeedVideoCompleted, trackFeedVideoSkipped } =
    useTrackFeedEvents();

  // Infinite scroll: prefetch the next page as the user nears the end.
  useEffect(() => {
    if (hasMore && currentIndex >= chapters.length - LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [currentIndex, chapters.length, hasMore, onLoadMore]);

  // The end-of-content panel sits at the virtual index === chapters.length, only
  // once the feed is exhausted (mirrors iOS pushing a chapterID === -1 marker).
  const showEndScreen = !hasMore && chapters.length > 0;
  const endIndex = showEndScreen ? chapters.length : -1;
  // currentIndex can be the virtual end index when the user scrolled onto it.
  const isOnEndScreen = currentIndex === endIndex;

  const scrollRef = useRef<HTMLDivElement>(null);
  const isResettingScroll = useRef(false);
  const scrollPausedByUs = useRef(false);
  const lastViewedChapterId = useRef<number | null>(null);
  // The chapter we're leaving when a new one becomes active, so we can classify
  // it COMPLETED vs SKIPPED off its first lesson's accumulated watch time before
  // that lesson's max-position accumulator is reset for the new active clip.
  const prevVideo = useRef<{ chapterId: number; lessonId: number } | null>(null);

  const hasPrevious = currentIndex > 0;
  // "next" exists while there are more chapters OR an end screen to scroll onto.
  const hasNext = isOnEndScreen
    ? false
    : currentIndex < chapters.length - 1 || showEndScreen;
  const currentPanelIndex = hasPrevious ? 1 : 0;

  // Live geometry for the once-subscribed up/down-button handler (avoids stale
  // closures): which rendered panel is active + whether we can move each way.
  const navRef = useRef({ currentPanelIndex, hasPrevious, hasNext });
  navRef.current = { currentPanelIndex, hasPrevious, hasNext };

  // Track chapter view + feed_video_scrolled when a new chapter becomes active
  // (iOS fires trackFeedVideoScrolled on every chapter change, FeedNew.tsx:432).
  // Also drives the feed-video lifecycle telemetry:
  //   - feed_video_viewed: once per chapter that becomes active.
  //   - feed_video_completed / feed_video_skipped: classify the chapter we're
  //     LEAVING by how much of its first lesson's clip its accumulated watch
  //     time covered (>= VIDEO_COMPLETED_FRACTION → completed, else skipped).
  // These three feed the session completion_rate / skip_rate / engagement_score
  // that feed_session_ended reports — without them those rates are always 0.
  useEffect(() => {
    const chapter = chapters[currentIndex];
    // The active panel is either a real chapter or the end-of-content marker
    // (iOS uses chapterID === -1). Track the marker too so the LAST reel gets
    // classified completed/skipped when the user scrolls past it onto the end
    // screen — otherwise the final video is silently dropped from the session
    // completion/skip rates.
    const END_MARKER = -1;
    const activeId = chapter
      ? chapter.chapterID
      : isOnEndScreen
      ? END_MARKER
      : null;
    if (activeId !== null && activeId !== lastViewedChapterId.current) {
      // Classify the video we're leaving (if any) before recording the new one.
      const prev = prevVideo.current;
      if (prev) {
        const watchedMs = maxPositionReachedMs$[prev.lessonId].peek() ?? 0;
        const watchTimeSec = Math.round(watchedMs / 1000);
        const clip = lessonDataCache$[prev.lessonId].peek()?.video;
        const clipLenMs = clip ? Math.max(1, clip.end - clip.start) : 0;
        if (clipLenMs > 0 && watchedMs / clipLenMs >= VIDEO_COMPLETED_FRACTION) {
          trackFeedVideoCompleted(prev.chapterId, watchTimeSec);
        } else {
          trackFeedVideoSkipped(prev.chapterId, watchTimeSec);
        }
      }

      lastViewedChapterId.current = activeId;
      if (chapter) {
        prevVideo.current = { chapterId: chapter.chapterID, lessonId: chapter.lessonIDs[0] };
        onChapterViewed(chapter.chapterID);
        // Count this chapter as a viewed video (source matches the feed surface
        // tag the rest of the feed events carry).
        trackFeedVideoViewed(chapter.chapterID, "video_feed");
        trackFeedVideoScrolled(
          chapter.chapterID,
          chapter.lessonIDs[0],
          currentIndex,
          0,
        );
      } else {
        // Landed on the end screen — nothing new to view, and we've already
        // classified the reel we left. Clear so scrolling back and forth onto
        // the end screen can't re-classify the same reel twice.
        prevVideo.current = null;
      }
    }
  }, [
    currentIndex,
    isOnEndScreen,
    chapters,
    onChapterViewed,
    trackFeedVideoScrolled,
    trackFeedVideoViewed,
    trackFeedVideoCompleted,
    trackFeedVideoSkipped,
  ]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    isResettingScroll.current = true;
    scrollPausedByUs.current = false;
    el.scrollTo({ top: currentPanelIndex * el.clientHeight, behavior: "instant" as ScrollBehavior });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isResettingScroll.current = false;
      });
    });
  }, [currentIndex, currentPanelIndex]);

  const handleScrollEnd = useCallback(() => {
    if (isResettingScroll.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const panelHeight = el.clientHeight;
    if (panelHeight === 0) return;
    const snappedIndex = Math.round(el.scrollTop / panelHeight);
    if (snappedIndex < currentPanelIndex && hasPrevious) {
      isPaused$.set(true);
      previousChapter();
    } else if (snappedIndex > currentPanelIndex && hasNext) {
      isPaused$.set(true);
      nextChapter();
    }
  }, [currentPanelIndex, hasPrevious, hasNext]);

  const handleScroll = useCallback(() => {
    if (isResettingScroll.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const panelHeight = el.clientHeight;
    if (panelHeight === 0) return;
    const currentTop = currentPanelIndex * panelHeight;
    const scrollFraction = Math.abs(el.scrollTop - currentTop) / panelHeight;
    if (scrollFraction >= 0.5 && !scrollPausedByUs.current) {
      if (!isPaused$.peek()) {
        scrollPausedByUs.current = true;
        isPaused$.set(true);
      }
    } else if (scrollFraction < 0.5 && scrollPausedByUs.current) {
      scrollPausedByUs.current = false;
      isPaused$.set(false);
    }
  }, [currentPanelIndex]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // iOS Safari before ~26 doesn't fire `scrollend`. Without it the feed snaps
    // (CSS scroll-snap + the touchend snap timer) but handleScrollEnd never runs,
    // so feedCurrentIndex$ never advances and the PWA stays stuck on a panel.
    // Feature-detect it; when absent, settle via a debounced scroll+touchend
    // timer that also advances state.
    const hasScrollEnd = "onscrollend" in (el as object);
    if (hasScrollEnd) el.addEventListener("scrollend", handleScrollEnd);
    el.addEventListener("scroll", handleScroll, { passive: true });

    // Fallback settle: a scroll that goes quiet for the settle window is treated
    // as a scrollend — snap to the nearest panel, then advance state.
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const onScrollSettle = () => {
      if (hasScrollEnd || isResettingScroll.current) return;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const h = el.clientHeight;
        if (h) {
          const target = Math.round(el.scrollTop / h) * h;
          if (Math.abs(el.scrollTop - target) > SNAP_TOLERANCE_PX) {
            el.scrollTo({ top: target, behavior: "smooth" });
          }
        }
        handleScrollEnd();
      }, 120);
    };
    if (!hasScrollEnd) el.addEventListener("scroll", onScrollSettle, { passive: true });

    let snapTimer: ReturnType<typeof setTimeout> | undefined;
    const onTouchEnd = () => {
      clearTimeout(snapTimer);
      let hadMomentum = false;
      const onScroll = () => { hadMomentum = true; };
      el.addEventListener("scroll", onScroll, { passive: true });
      snapTimer = setTimeout(() => {
        el.removeEventListener("scroll", onScroll);
        if (hadMomentum) return;
        const h = el.clientHeight;
        if (!h) return;
        const target = Math.round(el.scrollTop / h) * h;
        if (Math.abs(el.scrollTop - target) > SNAP_TOLERANCE_PX) {
          el.scrollTo({ top: target, behavior: "smooth" });
        }
        // No momentum scroll fired, so the settle handler above won't run —
        // advance state here too (only needed on the no-scrollend path).
        if (!hasScrollEnd) handleScrollEnd();
      }, MOMENTUM_SETTLE_DELAY_MS);
    };
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      clearTimeout(snapTimer);
      clearTimeout(settleTimer);
      el.removeEventListener("scrollend", handleScrollEnd);
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("scroll", onScrollSettle);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [handleScrollEnd, handleScroll]);

  // Up/down nav-button taps: drive the SAME smooth vertical scroll a swipe
  // up/down settles through, instead of nextChapter()/previousChapter()'s
  // instant index jump. We smooth-scroll to the adjacent panel; the scrollend /
  // settle path above then advances feedCurrentIndex$, so the on-screen reel
  // transition is identical to a swipe. Subscribed once; reads live geometry
  // from navRef to avoid stale closures.
  useEffect(() => {
    return feedVerticalScrollRequest$.onChange(({ value }) => {
      const el = scrollRef.current;
      if (!el || isResettingScroll.current) return;
      const h = el.clientHeight;
      if (!h) return;
      const { currentPanelIndex: active, hasPrevious: prev, hasNext: next } =
        navRef.current;
      const forward = value.direction === "next";
      if (forward ? !next : !prev) return;
      const targetIndex = forward ? active + 1 : active - 1;
      el.scrollTo({ top: targetIndex * h, behavior: "smooth" });
    });
    // feedVerticalScrollRequest$ is a stable module-level observable; subscribe once.
  }, []);

  if (chapters.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
      </div>
    );
  }

  const panels: { index: number; slot: "previous" | "current" | "next" }[] = [];
  if (hasPrevious) panels.push({ index: currentIndex - 1, slot: "previous" });
  panels.push({ index: currentIndex, slot: "current" });
  if (hasNext) panels.push({ index: currentIndex + 1, slot: "next" });

  return (
    <div
      ref={scrollRef}
      className="h-full w-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      style={{ scrollSnapType: "y mandatory", overscrollBehaviorY: "contain" }}
    >
      {panels.map((panel) => {
        const isActive = panel.slot === "current";
        // End-of-content virtual panel (index === chapters.length).
        if (panel.index === endIndex) {
          return (
            <div
              key={panel.slot === "current" ? "active" : "end"}
              className="w-full overflow-hidden"
              style={{ height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always" }}
            >
              <EndOfContentScreen />
            </div>
          );
        }
        const chapter = chapters[panel.index];
        if (!chapter) return null;
        return (
          <div
            key={panel.slot === "current" ? "active" : panel.index}
            className="w-full overflow-hidden"
            style={{ height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always" }}
          >
            {isActive ? (
              <HorizontalLessonCarousel
                chapter={chapter}
                onToggleLike={() => onToggleLike(chapter.chapterID, !!likedChapters$[chapter.chapterID]?.get())}
              />
            ) : (
              <VideoLayout
                chapterId={chapter.chapterID}
                lessonId={chapter.lessonIDs[0]}
                isActive={false}
                onToggleLike={() => {}}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

export { VerticalVideoCarousel };
